package com.devi3east.bashbuddy.features.common.ui.components

import Config
import androidx.compose.foundation.Image
import androidx.compose.foundation.LocalIndication
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.asPaddingValues
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBars
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Leaderboard
import androidx.compose.material3.Button
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import bashbuddy.composeapp.generated.resources.Res
import bashbuddy.composeapp.generated.resources.bash_buddy_topbar_logo
import com.devi3east.bashbuddy.account.profile.domain.models.Profile
import com.devi3east.bashbuddy.auth.domain.models.UserType
import com.devi3east.bashbuddy.features.app.data.AppState
import com.devi3east.bashbuddy.features.app.data.Screen
import org.jetbrains.compose.resources.painterResource

@Composable
fun BashBuddyTopBar(
    appState: AppState,
    profile: Profile?,
    onSignUp: () -> Unit = { appState.authViewModel.onSignUpSelected() },
    onSignIn: () -> Unit = { appState.authViewModel.onSignInSelected() },
    onNavigateToCart: () -> Unit = { appState.navController.navigate(Screen.CartScreen.route) },
    leaderBoardVisible: Boolean = false,
    onNavigateToLeaderboard: () -> Unit = { appState.navController.navigate(Screen.LeaderboardScreen.route) }
) {
    val displayName = profile?.displayName?.takeIf { it.isNotBlank() }
    val uiState by appState.cartViewModel.uiState.collectAsState()
    val cartItemCount by remember(uiState) { derivedStateOf { uiState.items.sumOf { it.quantity } } }
    val interactionSource = remember { MutableInteractionSource() }
    val eventFeedUiState by appState.eventFeedViewModel.uiState.collectAsState()
    val appConfigUiState by appState.appConfigViewModel.uiState.collectAsState()
    val userCount = eventFeedUiState.userCount

    Column(
        modifier = Modifier
            .background(MaterialTheme.colorScheme.background)
            .fillMaxWidth()
            .padding(WindowInsets.statusBars.asPaddingValues())
            .padding(horizontal = 16.dp, vertical = 12.dp)
    ) {
        // ───── Main Top Row ─────
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Row(
                modifier = Modifier.weight(1f),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Image(
                    painter = painterResource(
                        Res.drawable.bash_buddy_topbar_logo
                    ),
                    contentDescription = "bash buddy",
                    modifier = Modifier.height(48.dp),
                    contentScale = ContentScale.Fit
                )

                if (!Config.isProduction) {
                    Text(
                        text = "dev",
                        modifier = Modifier.padding(start = 8.dp),
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        style = MaterialTheme.typography.labelSmall,
                        fontWeight = FontWeight.Medium
                    )
                }
            }

            if (displayName != null) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.End,
                ) {
                    Text(
                        text = displayName,
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onBackground,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier
                            .padding(end = 8.dp)
                            .align(Alignment.CenterVertically)
                    )

                    if (profile.userType == UserType.USER) {
                        CartIconWithBadge(
                            cartItemCount = cartItemCount,
                            onClick = onNavigateToCart
                        )
                    }
                }
            } else {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.End
                ) {
                    TextButton(onClick = onSignUp) {
                        Text(
                            text = "Sign Up",
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            fontWeight = FontWeight.Medium
                        )
                    }
                    Button(
                        onClick = onSignIn,
                        modifier = Modifier.padding(start = 8.dp),
                        shape = RoundedCornerShape(12.dp)
                    ) {
                        Text(
                            text = "Sign In",
                            fontWeight = FontWeight.SemiBold
                        )
                    }
                }
            }
        }

        if (leaderBoardVisible && appConfigUiState.leaderboardPromotionEnabled) {
            // ───── Promo Row ─────
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(top = 10.dp)
                    .clip(RoundedCornerShape(12.dp))
                    .background(MaterialTheme.colorScheme.primary)
                    .clickable(
                        interactionSource = interactionSource,
                        indication = LocalIndication.current,
                        onClick = onNavigateToLeaderboard
                    )
                    .padding(vertical = 10.dp, horizontal = 16.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Left icon (fixed)
                Icon(
                    imageVector = Icons.Default.Leaderboard,
                    contentDescription = "Leaderboard",
                    tint = MaterialTheme.colorScheme.onPrimary
                )

                Spacer(Modifier.width(12.dp))

                // Wrapping text column (takes remaining space)
                Column(
                    modifier = Modifier.weight(1f),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    Text(
                        text = "View Organization",
                        color = MaterialTheme.colorScheme.onPrimary,
                        style = MaterialTheme.typography.titleMedium.copy(
                            fontWeight = FontWeight.Bold
                        ),
                        maxLines = 1
                    )

                    Text(
                        text = "Leaderboard",
                        color = MaterialTheme.colorScheme.onPrimary.copy(alpha = 0.9f),
                        style = MaterialTheme.typography.bodyMedium,
                        maxLines = 1
                    )
                }

                Spacer(Modifier.width(12.dp))

                // Right icon (fixed)
                Icon(
                    imageVector = Icons.Default.Leaderboard,
                    contentDescription = "Leaderboard",
                    tint = MaterialTheme.colorScheme.onPrimary
                )
            }

            /*Spacer(modifier = Modifier.height(10.dp))

            UserCounterBanner(
                userCount = userCount
            )*/
        }

    }
}